/*
 * IdleScreen — landing hero, query input, suggested-query chips, corpus grid.
 * Ported from ui-handoff/.../app.jsx::IdleScreen.
 *
 * The headline used a hard-coded "12.4k frames" figure; the real corpus has
 * 13 × 200 ≈ 2,600 frames. We surface the actual number from /corpus so the
 * design stays honest as the corpus grows.
 */

import React from 'react';
import { CorpusGrid } from './Corpus.jsx';

const SUGGESTED_QUERIES = [
  'right turn at an intersection',
  'driving at nighttime in rainy weather',
  'pedestrian crossing in low light',
  'parking lot maneuver',
  'foggy weather conditions',
];

export function IdleScreen({ query, setQuery, onRun, corpusFrameCount }) {
  const frameLabel = corpusFrameCount
    ? corpusFrameCount.toLocaleString()
    : '—';

  return (
    <div className="idle fade-in">
      <section className="idle-hero">
        <h1 className="idle-h1 serif">
          Find <em>edge cases</em> across <span className="num">{frameLabel}</span> dashcam frames
          <br />
          by describing the moment in plain English.
        </h1>

        <form
          className="idle-form"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = query.trim();
            if (trimmed) onRun(trimmed);
          }}
        >
          <div className="idle-input-wrap">
            <span className="idle-prompt mono">▸</span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="idle-input serif"
              placeholder="e.g. right turn at an intersection with a cyclist"
              autoFocus
            />
            <button type="submit" className="idle-run mono" disabled={!query.trim()}>
              run
              <svg viewBox="0 0 24 24" width="12" height="12">
                <path d="M5 12 H19 M13 6 L19 12 L13 18" fill="none" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </button>
          </div>
          <div className="idle-suggest">
            <span className="mono micro">SUGGESTED</span>
            <div className="idle-suggest-chips">
              {SUGGESTED_QUERIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="idle-chip mono"
                  onClick={() => setQuery(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </form>
      </section>

      <CorpusGrid />

      <style>{`
        .idle { padding-top: 80px; }
        .idle-hero { padding: 0 64px 56px; max-width: 1180px; margin: 0 auto; }
        .idle-h1 {
          font-size: 46px; line-height: 1.12; letter-spacing: -0.015em;
          font-weight: 300; color: var(--ink); margin: 0 0 44px;
          max-width: 920px; text-wrap: balance;
        }
        .idle-h1 em { font-style: italic; color: var(--accent); }
        .idle-form { display: flex; flex-direction: column; gap: 18px; max-width: 920px; }
        .idle-input-wrap {
          display: flex; align-items: center;
          border-top: 1px solid var(--ink);
          border-bottom: 1px solid var(--ink);
          padding: 8px 4px;
          gap: 12px;
        }
        .idle-prompt { font-size: 16px; color: var(--accent); padding-left: 6px; }
        .idle-input {
          flex: 1; min-width: 0;
          background: transparent; border: 0; outline: 0;
          font-size: 24px; padding: 14px 4px;
          color: var(--ink);
          font-style: italic; font-weight: 300;
        }
        .idle-input::placeholder { color: var(--ink-4); }
        .idle-input:focus { outline: none; }
        .idle-run {
          background: var(--ink); color: var(--bg); border: 0;
          padding: 12px 18px; font-size: 12px; letter-spacing: 0.16em; text-transform: uppercase;
          display: inline-flex; align-items: center; gap: 8px; font-weight: 500;
        }
        .idle-run:hover:not(:disabled) { background: var(--accent); }
        .idle-run:disabled { background: var(--ink-4); cursor: not-allowed; }
        .idle-suggest { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
        .idle-suggest-chips { display: flex; gap: 6px; flex-wrap: wrap; }
        .idle-chip {
          background: transparent; border: 1px solid var(--rule-2); color: var(--ink-2);
          padding: 5px 10px; font-size: 11px; letter-spacing: 0.02em;
        }
        .idle-chip:hover { color: var(--accent); border-color: var(--accent); background: var(--accent-2); }
      `}</style>
    </div>
  );
}
